import { useTranslation } from "react-i18next";
import { DollarOutlined, EnvironmentOutlined, HistoryOutlined } from "@ant-design/icons";
import { Flex } from "antd";
import { createStyles } from "antd-style";

import { toApplicationCloseDate } from "@av/apps/recruitment/ats-common/application-close-date";
import { Typography } from "@av/components/atoms/typography/Typography";
import { type Dayjs } from "@av/config/extended-dayjs";
import { useLocaleStore } from "@av/utils/hooks/useLocaleStore";
import { JobPostAddress } from "./JobPostAddress";
import { JobPostCompensation } from "./JobPostCompensation";
import { getLocalizedJobPostContent } from "../../localized-job-post-content";
import { JobLocationArrangment } from "../JobLocationArrangment";

import type {
  CurrencyCode,
  EmploymentType,
  LocationType,
  PayDisplay,
  PayType,
  Province,
} from "../../types";

const { Title, Text } = Typography;

const useStyles = createStyles(({ token, css }) => ({
  detailContent: css`
    padding: ${token.paddingLG}px;
  `,
  titleWrapper: css`
    flex: 1 1 auto;
    min-width: 0;
  `,
  jobId: css`
    font-size: ${token.fontSizeSM}px;
  `,
  jobTitle: css`
    width: 300px;
  `,
  additionalItems: css`
    flex-shrink: 0;
    min-height: 30px;
    white-space: nowrap;
  `,
  metaItem: css`
    align-items: center;
    gap: ${token.marginXXS}px;
  `,
  icons: css`
    color: ${token["blue-6"]};
  `,
}));

type JobPostProp = {
  jobTitles: Record<string, string>;
  jobId?: number;
  locationType?: LocationType;
  city?: string;
  streetAddress?: string;
  subdivisionCode?: Province;
  employmentType?: EmploymentType;
  minCompensation?: number;
  maxCompensation?: number;
  currencyCode?: CurrencyCode;
  includeCompensationOnJobPost: boolean;
  applyBy?: Dayjs;
  payType?: PayType;
  payDisplay?: PayDisplay;
};

interface JobPostDetailProps {
  jobPost: JobPostProp;
  jobContentAdditionalItems?: React.ReactNode;
  testIdPrefix: string;
  jobPostContent: React.ReactNode;
  isPublic?: boolean;
}

export function JobPostDetail(props: JobPostDetailProps) {
  const { testIdPrefix, jobPost, jobContentAdditionalItems, jobPostContent } = props;
  const { styles } = useStyles();
  const { t } = useTranslation();
  const { locale } = useLocaleStore();
  const jobTitle = getLocalizedJobPostContent(jobPost.jobTitles, locale);

  return (
    <div>
      <Flex vertical gap={31} className={styles.detailContent}>
        <Flex vertical gap={10}>
          <Flex justify="space-between" align="center" gap={10}>
            <Flex vertical className={styles.titleWrapper}>
              <Title
                className={!!jobContentAdditionalItems ? styles.jobTitle : undefined}
                level={2}
                ellipsis={{ rows: 2, tooltip: jobTitle }}
                testIdPrefix={`${testIdPrefix}-position`}
              >
                {jobTitle}
              </Title>
              {jobPost.jobId && (
                <Text
                  type="secondary"
                  className={styles.jobId}
                  testIdPrefix={`${testIdPrefix}-job-id`}
                >
                  {t("recruitment.talentPool.jobId", { jobId: String(jobPost.jobId) })}
                </Text>
              )}
            </Flex>
            <Flex className={styles.additionalItems}>{jobContentAdditionalItems}</Flex>
          </Flex>
          <Flex vertical gap={2}>
            <Flex className={styles.metaItem}>
              <EnvironmentOutlined className={styles.icons} />
              <Text testIdPrefix={`${testIdPrefix}-location-arrangement`}>
                <JobLocationArrangment
                  locationType={jobPost.locationType}
                  city={jobPost.city}
                  subdivisionCode={jobPost.subdivisionCode}
                  employmentType={jobPost.employmentType}
                />
              </Text>
            </Flex>
            <JobPostAddress
              city={jobPost.city}
              streetAddress={jobPost.streetAddress}
              subdivisionCode={jobPost.subdivisionCode}
              testIdPrefix={testIdPrefix}
            />
          </Flex>
          {(!props.isPublic || jobPost.includeCompensationOnJobPost) && (
            <Flex className={styles.metaItem}>
              <DollarOutlined className={styles.icons} />
              <Flex gap={4} align="center">
                <JobPostCompensation
                  minCompensation={jobPost.minCompensation}
                  maxCompensation={jobPost.maxCompensation}
                  payDisplay={jobPost.payDisplay}
                  currencyCode={jobPost.currencyCode}
                  payType={jobPost.payType}
                  locale={locale}
                  testIdPrefix={testIdPrefix}
                />
                <Text testIdPrefix={`${testIdPrefix}-pay-type`}>
                  /{" "}
                  {jobPost.payType === "SALARY"
                    ? t("recruitment.jobs.addJob_compensation_frequencyUnits_SALARY")
                    : t("recruitment.jobs.addJob_compensation_frequencyUnits_HOURLY")}
                </Text>
              </Flex>
            </Flex>
          )}
          {props.isPublic && jobPost.applyBy && (
            <Flex className={styles.metaItem}>
              <HistoryOutlined className={styles.icons} />
              <Text testIdPrefix={`${testIdPrefix}-apply-by`}>
                {t("recruitment.jobApplication.acceptingApplicationsUntil", {
                  date: toApplicationCloseDate(jobPost.applyBy)?.format(
                    t("common.components.dateFormat")
                  ),
                })}
              </Text>
            </Flex>
          )}
        </Flex>

        {jobPostContent}
      </Flex>
    </div>
  );
}
